/**
 * ===================================================================================================
 * SuggestionModal.js - AI 일정 제안 관리 모달
 * ===================================================================================================
 *
 * 설명: 채팅방의 AI 일정 제안들을 지난/오늘/미래로 분류하여 보여주고 개인별 참석/불참 응답 관리
 *
 * 기능:
 * - 지난 약속, 오늘 약속, 미래 약속 탭 전환
 * - 멤버별 응답 상태 표시 (pending/accepted/rejected)
 * - 개인별 [참석]/[불참] 버튼
 * - 실시간 업데이트 (Socket.io suggestion-updated 이벤트)
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import { X, Calendar, Clock, MapPin, Users, Check, XCircle, Trash2, Star } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import { io } from 'socket.io-client';
import ScheduleDetailModal from './ScheduleDetailModal';
import CustomAlertModal from '../modals/CustomAlertModal';

import { API_BASE_URL } from '../../config/apiConfig';

const REJECTION_REASONS = [
  { value: 'distance', label: '거리가 멀어요' },
  { value: 'food', label: '음식이나 메뉴가 맞지 않아요' },
  { value: 'time', label: '시간이 맞지 않아요' },
  { value: 'participants', label: '참석자 구성이 맞지 않아요' },
  { value: 'atmosphere', label: '분위기가 맞지 않아요' },
  { value: 'other', label: '기타' },
];

const DEFAULT_OUTCOME = {
  attendance: 'attended',
  completed: true,
  satisfaction: 4,
  wouldChooseAgain: true,
  notes: '',
};

const SuggestionModal = ({ isOpen, onClose, roomId, socket: externalSocket, isMobile }) => {
  const { showToast } = useToast();
  const [ownSocket, setOwnSocket] = useState(null);
  const socketRef = useRef(null);

  // 외부 소켓이 제공되지 않으면 자체 생성
  useEffect(() => {
    if (isOpen && !externalSocket) {
      const newSocket = io(API_BASE_URL, { transports: ['websocket', 'polling'] });
      socketRef.current = newSocket;

      newSocket.on('connect', () => {
        newSocket.emit('join-room', roomId);
      });

      setOwnSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [isOpen, roomId, externalSocket]);

  // 사용할 소켓 결정
  const socket = externalSocket || ownSocket;
  const [activeTab, setActiveTab] = useState('future'); // 'past' | 'today' | 'future'
  const [suggestions, setSuggestions] = useState({
    past: [],
    today: [],
    future: []
  });
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // 🆕 사용자 전체 프로필 (주소 포함)
  const [selectedSuggestion, setSelectedSuggestion] = useState(null); // 🆕 상세 모달용
  const [memberLocations, setMemberLocations] = useState([]); // 방 멤버 GPS 위치 (지도 핀 표시용)
  const [conflictModal, setConflictModal] = useState(null); // 🆕 충돌 확인 모달
  const [alternativeCandidates, setAlternativeCandidates] = useState({}); // ARES 대안 일정
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [rejectDrafts, setRejectDrafts] = useState({});
  const [outcomeDrafts, setOutcomeDrafts] = useState({});

  // 현재 사용자 정보 가져오기 (email로 비교)
  useEffect(() => {
    if (isOpen && auth.currentUser) {
      setCurrentUser({
        _id: auth.currentUser.uid,
        email: auth.currentUser.email
      });

      // 🆕 사용자 전체 프로필 가져오기 (주소 포함)
      fetchUserProfile();
    }
  }, [isOpen]);

  // 방 멤버 GPS 위치 fetch (지도 핀 표시용)
  // - 열릴 때마다 내 GPS를 먼저 갱신 후 멤버 위치 조회 (최신 위치 반영)
  useEffect(() => {
    if (!isOpen || !roomId) return;
    const fetchMemberLocations = async () => {
      try {
        const uid = auth.currentUser?.uid;
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        // 1) 내 GPS 갱신 (navigator.geolocation 지원 시)
        if (uid && navigator.geolocation) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              async (pos) => {
                try {
                  await fetch(`${API_BASE_URL}/api/engine/dg/location/update`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ userId: uid, lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
                  });
                } catch (_) {}
                resolve();
              },
              () => resolve(), // 권한 거부 시 그냥 계속 진행
              { timeout: 5000, maximumAge: 60000 }
            );
          });
        }

        // 2) 멤버 위치 조회 (활성 제안 ID 포함 → 불참자 제외 + 커스텀 출발지 반영)
        // today → future 순으로 활성 제안 선택 (ABSTAIN 후 새 제안의 불참자·평택 출발지 적용)
        const activeSuggFromState = suggestions.today?.[0] || suggestions.future?.[0] || null;
        const memberQueryStr = activeSuggFromState ? `?suggestionId=${activeSuggFromState._id}` : '';
        const res = await fetch(`${API_BASE_URL}/api/engine/room-members/${roomId}${memberQueryStr}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.locations)) setMemberLocations(data.locations);
      } catch (_) {}
    };
    fetchMemberLocations();
  }, [isOpen, roomId]);

  // 🆕 사용자 프로필 가져오기
  const fetchUserProfile = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  // 상세보기 열기 (memberLocations에 isSelf 기준 출발지 포함)
  const openDetail = (suggestion) => setSelectedSuggestion(suggestion);

  // 제안 목록 불러오기
  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
    }
  }, [isOpen, roomId]);

  // Socket 실시간 업데이트 구독
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handleSuggestionUpdated = (data) => {
      // Bug 1 수정: 새 제안 생성(status='created') 이벤트 → 목록 전체 재조회
      if (data?.status === 'created') {
        fetchSuggestions();
        return;
      }
      // 출발지변경 재배치로 취소된 제안 → 목록에서 즉시 제거
      if (data?.status === 'cancelled') {
        setSuggestions((prev) => {
          const updated = { ...prev };
          for (const category of ['past', 'today', 'future']) {
            updated[category] = updated[category].filter(s => s._id !== data.suggestionId);
          }
          return updated;
        });
        return;
      }
      // 실시간 업데이트 - 해당 제안 찾아서 교체
      setSuggestions((prev) => {
        const updatedSuggestions = { ...prev };

        // 모든 카테고리에서 해당 제안 찾기
        for (const category of ['past', 'today', 'future']) {
          const index = updatedSuggestions[category].findIndex(
            (s) => s._id === data.suggestionId
          );

          if (index !== -1) {
            // 기존 제안 업데이트 (memberResponses + suggestedBy + location/date/time 반영)
            updatedSuggestions[category][index] = {
              ...updatedSuggestions[category][index],
              ...(data.memberResponses !== undefined && { memberResponses: data.memberResponses }),
              ...(data.suggestedBy !== undefined && { suggestedBy: data.suggestedBy }),
              ...(data.location !== undefined && { location: data.location }),
              ...(data.date !== undefined && { date: data.date }),
              ...(data.startTime !== undefined && { startTime: data.startTime }),
              ...(data.endTime !== undefined && { endTime: data.endTime }),
            };
            break;
          }
        }

        return updatedSuggestions;
      });
    };

    const handleSuggestionDeleted = (data) => {
      // 삭제된 제안을 목록에서 제거
      setSuggestions((prev) => {
        const updated = { ...prev };
        for (const category of ['past', 'today', 'future']) {
          updated[category] = updated[category].filter(
            (s) => s._id !== data.suggestionId
          );
        }
        return updated;
      });
    };

    socket.on('suggestion-updated', handleSuggestionUpdated);
    socket.on('suggestion-deleted', handleSuggestionDeleted);

    // 재참석/재구성 시 새 제안 Z가 생성될 때 목록 갱신
    const handleNewSuggestion = () => {
      fetchSuggestions();
    };
    socket.on('new-suggestion', handleNewSuggestion);

    // 🆕 충돌 확인 필요 이벤트
    const handleConflictConfirmation = (data) => {
      // 현재 사용자에게만 모달 표시
      if (userProfile && data.targetUserId === userProfile._id) {
        setConflictModal(data);
      }
    };
    socket.on('conflict-confirmation-needed', handleConflictConfirmation);

    return () => {
      socket.off('suggestion-updated', handleSuggestionUpdated);
      socket.off('suggestion-deleted', handleSuggestionDeleted);
      socket.off('new-suggestion', handleNewSuggestion);
      socket.off('conflict-confirmation-needed', handleConflictConfirmation);
    };
  }, [socket, isOpen]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();

      // 세 가지 상태 모두 가져오기
      const [pastRes, todayRes, futureRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions?status=past`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions?status=today`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions?status=future`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const past = await pastRes.json();
      const today = await todayRes.json();
      const future = await futureRes.json();

      setSuggestions({ past, today, future });

      // [3단계 수정] selectedSuggestion이 취소된 경우 새 활성 제안으로 자동 전환
      // 불참 재구성: 구 제안 cancelled → 새 제안 created → fetchSuggestions 호출됨
      // 이 시점에 today/future 목록에 구 제안이 없으면 취소된 것 → 새 제안 자동 선택
      setSelectedSuggestion(prev => {
        if (!prev) return prev;
        const activeList = [...(today || []), ...(future || [])];
        const stillActive = activeList.some(s => s._id === prev._id);
        if (!stillActive && activeList.length > 0) {
          console.log('[SuggestionModal] 재구성 감지: selectedSuggestion 자동 전환 →', activeList[0]._id);
          return activeList[0];
        }
        return prev;
      });
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (suggestionId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        // fetchSuggestions()를 즉시 호출하지 않음 → 소켓 이벤트에 위임
        // 일반 참석: suggestion-updated(accepted) → 인플레이스 업데이트
        // 재참석(rejoin): new-suggestion(Z) → fetchSuggestions() 호출됨
        // 이유: HTTP 200이 async IIFE(Y 취소+Z 생성)보다 먼저 도착해 stale [Y]가 [Z]를 덮어쓰는 경쟁조건 방지
        window.dispatchEvent(new CustomEvent('suggestionUpdate', { detail: { roomId } }));
      } else {
        const error = await res.json();
        showToast(error.message || '일정 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to accept suggestion:', error);
      showToast('일정 추가 중 오류가 발생했습니다.');
    }
  };

  const openRejectForm = (suggestionId) => {
    setRejectDrafts(prev => ({
      ...prev,
      [suggestionId]: { reasonCode: '', reasonText: '', submitting: false },
    }));
  };

  const closeRejectForm = (suggestionId) => {
    setRejectDrafts(prev => {
      const next = { ...prev };
      delete next[suggestionId];
      return next;
    });
  };

  const updateRejectDraft = (suggestionId, patch) => {
    setRejectDrafts(prev => {
      if (!prev[suggestionId]) return prev;
      return {
        ...prev,
        [suggestionId]: { ...prev[suggestionId], ...patch },
      };
    });
  };

  const handleReject = async (suggestionId) => {
    const draft = rejectDrafts[suggestionId];
    if (!draft?.reasonCode) {
      showToast('불참 사유를 선택해 주세요.');
      return;
    }

    updateRejectDraft(suggestionId, { submitting: true });
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reasonCode: draft.reasonCode,
          reasonText: draft.reasonText,
        }),
      });

      if (res.ok) {
        const data = await res.json();

        if (data.action === 'deleted') {
          showToast('일정이 삭제되었습니다.');
        } else {
          showToast('불참 처리되었습니다.');
        }
        // Bug 1 수정: 항상 목록 재조회 → 새 A+B 제안 반영
        fetchSuggestions();

        closeRejectForm(suggestionId);

        window.dispatchEvent(new CustomEvent('calendarUpdate', { detail: { type: 'suggestion_rejected' } }));
        window.dispatchEvent(new CustomEvent('suggestionUpdate', { detail: { roomId } }));
      } else {
        const error = await res.json();
        showToast(error.msg || error.message || '일정 거절에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to reject suggestion:', error);
      showToast('일정 거절 중 오류가 발생했습니다.');
    } finally {
      updateRejectDraft(suggestionId, { submitting: false });
    }
  };

  const updateOutcomeDraft = (suggestionId, patch) => {
    setOutcomeDrafts(prev => ({
      ...prev,
      [suggestionId]: { ...DEFAULT_OUTCOME, ...prev[suggestionId], ...patch },
    }));
  };

  const handleOutcomeSubmit = async (suggestionId) => {
    const draft = { ...DEFAULT_OUTCOME, ...outcomeDrafts[suggestionId] };
    updateOutcomeDraft(suggestionId, { submitting: true });
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}/outcome`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.msg || '결과 기록에 실패했습니다.');
        return;
      }
      const synced = data.outcomeFeedback?.learningSync?.status === 'synced';
      showToast(synced ? '일정 결과가 학습에 반영되었습니다.' : '결과를 저장했습니다. 학습 엔진에는 자동으로 재전송됩니다.');
      await fetchSuggestions();
    } catch (error) {
      console.error('Failed to submit outcome feedback:', error);
      showToast('결과 기록 중 오류가 발생했습니다.');
    } finally {
      updateOutcomeDraft(suggestionId, { submitting: false });
    }
  };

  // 🆕 강제 참석 (충돌 무시)
  const handleForceAccept = async (suggestionId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}/force-accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setConflictModal(null);
        fetchSuggestions();
        window.dispatchEvent(new CustomEvent('suggestionUpdate', { detail: { roomId } }));
      } else {
        const error = await res.json();
        showToast(error.message || '참석 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('Failed to force accept suggestion:', error);
      showToast('참석 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = (suggestionId) => {
    setConfirmModal({
      isOpen: true,
      title: '일정 제안 삭제',
      message: '정말로 이 일정 제안을 삭제하시겠습니까?',
      onConfirm: async () => {
        try {
          const token = await auth.currentUser?.getIdToken();
          const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
          });

          if (res.ok) {
            fetchSuggestions();
            window.dispatchEvent(new CustomEvent('suggestionUpdate', { detail: { roomId } }));
          } else {
            const error = await res.json();
            showToast(error.message || '일정 삭제에 실패했습니다.');
          }
        } catch (error) {
          console.error('Failed to delete suggestion:', error);
          showToast('일정 삭제 중 오류가 발생했습니다.');
        }
      }
    });
  };

  // 현재 사용자가 제안자인지 확인 (email로 비교)
  const isOwner = (suggestion) => {
    if (!currentUser?.email) return false;
    // 제안자가 있으면 제안자만 삭제 가능
    if (suggestion.suggestedBy) {
      const suggestedByEmail = suggestion.suggestedBy.email;
      return suggestedByEmail === currentUser.email;
    }
    // 제안자가 나간 경우 (suggestedBy === null): rejected 아닌 멤버면 삭제 가능 (Bug 1 수정: pending 포함)
    const isActiveMember = suggestion.memberResponses?.some(
      r => r.user?.email === currentUser.email && r.status !== 'rejected'
    );
    return !!isActiveMember;
  };

  // 현재 사용자의 응답 상태 확인 (email로 비교)
  const getUserResponse = (suggestion) => {
    if (!currentUser?.email) return null;
    return suggestion.memberResponses?.find(
      (r) => r.user?.email === currentUser.email
    );
  };

  // 응답 통계 계산
  const getResponseStats = (suggestion) => {
    const total = suggestion.memberResponses?.length || 0;
    const accepted = suggestion.memberResponses?.filter((r) => r.status === 'accepted').length || 0;
    const rejected = suggestion.memberResponses?.filter((r) => r.status === 'rejected').length || 0;
    const pending = suggestion.memberResponses?.filter((r) => r.status === 'pending').length || 0;
    // 자동 불참 (일정 충돌)
    const autoRejected = suggestion.memberResponses?.filter((r) => r.isAutoRejected === true).length || 0;

    return { total, accepted, rejected, pending, autoRejected };
  };

  // 제안 시간 포맷 함수
  const formatSuggestionTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  const hasSuggestionEnded = (suggestion) => {
    const [hours, minutes] = String(suggestion.endTime || '').split(':').map(Number);
    const end = new Date(`${suggestion.date}T00:00:00`);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || Number.isNaN(end.getTime())) return false;
    end.setMinutes(hours * 60 + minutes);
    return end <= new Date();
  };

  // 제안 카드 렌더링
  const renderSuggestion = (suggestion) => {
    const userResponse = getUserResponse(suggestion);
    const stats = getResponseStats(suggestion);
    const canDelete = isOwner(suggestion);
    const rejectDraft = rejectDrafts[suggestion._id];
    const outcomeDraft = { ...DEFAULT_OUTCOME, ...outcomeDrafts[suggestion._id] };

    return (
      <div key={suggestion._id} className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm relative">
        {/* 제안 시간 (오른쪽 상단) */}
        {suggestion.createdAt && (
          <div className="absolute top-2 right-3 text-xs text-gray-400">
            {formatSuggestionTime(suggestion.createdAt)}
          </div>
        )}

        {/* 제안 정보 */}
        <div className="mb-3">
          <div className="flex items-start justify-between mb-2 pr-16">
            <div
              className="flex-1 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors"
              onClick={() => openDetail(suggestion)}
              title="클릭하여 상세보기"
            >
              <h3 className="text-lg font-bold text-gray-800">{suggestion.summary}</h3>
              {suggestion.suggestedBy && (
                <p className="text-xs text-gray-500 mt-0.5">
                  제안자: {suggestion.suggestedBy.firstName} {suggestion.suggestedBy.lastName}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {userResponse && (
                <span
                  className={`px-2 py-1 text-xs rounded-full font-medium ${
                    userResponse.status === 'accepted'
                      ? 'bg-green-100 text-green-700'
                      : userResponse.status === 'rejected'
                      ? 'bg-red-100 text-red-700'
                      : activeTab === 'past'
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {userResponse.status === 'accepted'
                    ? '참석'
                    : userResponse.status === 'rejected'
                    ? '불참'
                    : activeTab === 'past'
                    ? '종료됨'
                    : '대기중'}
                </span>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDelete(suggestion._id)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  title="삭제 (제안자만 가능)"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          <div
            className="space-y-1 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 -m-2 p-2 rounded transition-colors"
            onClick={() => openDetail(suggestion)}
            title="클릭하여 지도 및 경로 보기"
          >
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              <span>{suggestion.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>{suggestion.startTime} ~ {suggestion.endTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={14} className={suggestion.location ? 'text-blue-500' : ''} />
              <span className={suggestion.location ? 'text-blue-600 font-medium' : 'text-gray-400'}>
                {suggestion.location || '미정'}
              </span>
            </div>
            {suggestion.location && (
              <p className="text-xs text-blue-500 ml-5">📍 지도 및 대중교통 경로 보기</p>
            )}
            {/* 외부 참여자 표시 */}
            {suggestion.externalParticipants?.length > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <Users size={14} className="text-purple-500" />
                <span className="text-purple-600 font-medium">
                  {suggestion.externalParticipants.map(p => p.name).join(', ')} ({suggestion.externalParticipants.length}명)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 응답 통계 */}
        <div className="mb-3 pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Users size={14} />
            <span>총 {stats.total}명</span>
            <span className="text-green-600">✓ {stats.accepted}</span>
            <span className="text-red-600">✗ {stats.rejected}</span>
            <span className="text-yellow-600">⏳ {stats.pending}</span>
          </div>
        </div>

        {/* 멤버별 응답 상태 */}
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {suggestion.memberResponses?.map((response, idx) => (
              <div
                key={`member-${idx}`}
                className={`px-2 py-1 text-xs rounded border ${
                  response.status === 'accepted'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : response.isAutoRejected
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : response.status === 'rejected'
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
                title={response.isAutoRejected ? '일정 충돌로 자동 불참' : ''}
              >
                {response.user.firstName} {response.user.lastName}
                {response.status === 'accepted' && ' ✓'}
                {response.isAutoRejected && <XCircle size={12} className="inline ml-1" />}
                {response.status === 'rejected' && !response.isAutoRejected && ' ✗'}
              </div>
            ))}
          </div>
        </div>

        {/* 참석/불참 버튼 (pending 상태이고 미래/오늘 약속일 때만) */}
        {userResponse?.status === 'pending' && !rejectDraft && (activeTab === 'future' || activeTab === 'today') && (
          <div className="flex gap-2">
            <button
              onClick={() => handleAccept(suggestion._id)}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-1"
            >
              <Check size={16} /> 참석
            </button>
            <button
              onClick={() => openRejectForm(suggestion._id)}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-1"
            >
              <XCircle size={16} /> 불참
            </button>
          </div>
        )}

        {/* 자동 불참된 경우 - 참석 변경 버튼 표시 */}
        {userResponse?.isAutoRejected && (activeTab === 'future' || activeTab === 'today') && (
          <div className="space-y-2">
            <div className="text-center text-sm text-red-600 font-medium flex items-center justify-center gap-1">
              <XCircle size={14} /> 해당 시간에 일정이 있어 자동 불참 처리되었습니다
            </div>
            <button
              onClick={() => handleAccept(suggestion._id)}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-1"
            >
              <Check size={16} /> 참석으로 변경
            </button>
          </div>
        )}

        {/* 이미 응답한 경우 (수동 응답) - 변경 버튼 추가 */}
        {userResponse && userResponse.status !== 'pending' && !userResponse.isAutoRejected && !rejectDraft && (activeTab === 'future' || activeTab === 'today') && (
          <div className="space-y-2">
            <div className="text-center text-sm text-gray-500">
              {userResponse.status === 'accepted' ? '참석으로 응답하셨습니다' : '불참으로 응답하셨습니다'}
            </div>
            <button
              onClick={() => userResponse.status === 'accepted' ? openRejectForm(suggestion._id) : handleAccept(suggestion._id)}
              className={`w-full px-4 py-2 rounded-lg transition-colors font-medium flex items-center justify-center gap-1 ${
                userResponse.status === 'accepted' 
                  ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {userResponse.status === 'accepted' ? (
                <><XCircle size={16} /> 불참으로 변경</>
              ) : (
                <><Check size={16} /> 참석으로 변경</>
              )}
            </button>
          </div>
        )}

        {rejectDraft && (activeTab === 'future' || activeTab === 'today') && (
          <div className="space-y-2 border-t border-gray-200 pt-3">
            <select
              value={rejectDraft.reasonCode}
              onChange={(event) => updateRejectDraft(suggestion._id, { reasonCode: event.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              aria-label="불참 사유"
            >
              <option value="">불참 사유 선택</option>
              {REJECTION_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>{reason.label}</option>
              ))}
            </select>
            <textarea
              value={rejectDraft.reasonText}
              onChange={(event) => updateRejectDraft(suggestion._id, { reasonText: event.target.value })}
              maxLength={300}
              rows={2}
              placeholder="추가 내용 (선택)"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => closeRejectForm(suggestion._id)}
                className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => handleReject(suggestion._id)}
                disabled={rejectDraft.submitting || !rejectDraft.reasonCode}
                className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                불참 확정
              </button>
            </div>
          </div>
        )}
        {/* 지난 약속은 변경 버튼 없이 상태만 표시 */}
        {userResponse && userResponse.status !== 'pending' && !userResponse.isAutoRejected && activeTab === 'past' && (
          <div className="text-center text-sm text-gray-500">
            {userResponse.status === 'accepted' ? '참석으로 응답하셨습니다' : '불참으로 응답하셨습니다'}
          </div>
        )}

        {activeTab === 'past' && userResponse?.status === 'accepted' && hasSuggestionEnded(suggestion) && (
          userResponse.outcomeFeedback?.submittedAt ? (
            <div className="mt-3 border-t border-gray-200 pt-3 text-sm text-gray-600">
              <div className="flex items-center justify-between gap-2">
                <span>결과 기록 완료</span>
                <span className="font-medium text-gray-800">
                  만족도 {userResponse.outcomeFeedback.satisfaction || '-'} / 5
                </span>
              </div>
              {userResponse.outcomeFeedback.learningSync?.status === 'pending' && (
                <p className="mt-1 text-xs text-amber-700">학습 엔진 반영 대기 중</p>
              )}
            </div>
          ) : (
            <div className="mt-3 border-t border-gray-200 pt-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-gray-600">
                  참석 결과
                  <select
                    value={outcomeDraft.attendance}
                    onChange={(event) => updateOutcomeDraft(suggestion._id, { attendance: event.target.value })}
                    className="mt-1 w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                  >
                    <option value="attended">참석함</option>
                    <option value="partial">일부 참석</option>
                    <option value="missed">참석 못함</option>
                  </select>
                </label>
                <div className="text-xs text-gray-600">
                  만족도
                  <div className="mt-1 flex h-9 items-center gap-1" aria-label="만족도">
                    {[1, 2, 3, 4, 5].map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => updateOutcomeDraft(suggestion._id, { satisfaction: value })}
                        className="p-1 text-amber-500 hover:bg-amber-50 rounded"
                        title={`${value}점`}
                      >
                        <Star size={18} fill={value <= outcomeDraft.satisfaction ? 'currentColor' : 'none'} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={outcomeDraft.completed}
                    onChange={(event) => updateOutcomeDraft(suggestion._id, { completed: event.target.checked })}
                  />
                  일정이 정상 진행됨
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={outcomeDraft.wouldChooseAgain}
                    onChange={(event) => updateOutcomeDraft(suggestion._id, { wouldChooseAgain: event.target.checked })}
                  />
                  다시 선택할 의향 있음
                </label>
              </div>
              <textarea
                value={outcomeDraft.notes}
                onChange={(event) => updateOutcomeDraft(suggestion._id, { notes: event.target.value })}
                maxLength={500}
                rows={2}
                placeholder="결과 메모 (선택)"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
              />
              <button
                type="button"
                onClick={() => handleOutcomeSubmit(suggestion._id)}
                disabled={outcomeDraft.submitting}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                결과 저장
              </button>
            </div>
          )
        )}

        {/* ✨ ARES 대안 일정 박스 */}
        {alternativeCandidates[suggestion._id] && (() => {
          const handleAlternativeSelect = async (candidate) => {
            try {
              const token = await auth.currentUser?.getIdToken();
              const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/create-suggestion`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  summary: suggestion.summary || '그룹 모임',
                  date: candidate.date,
                  startTime: candidate.startTime,
                  endTime: candidate.endTime,
                  location: suggestion.location || ''
                })
              });
              if (res.ok) {
                showToast('✨ 대안 일정이 생성되었습니다!');
                setAlternativeCandidates(prev => {
                  const newState = { ...prev };
                  delete newState[suggestion._id];
                  return newState;
                });
                fetchSuggestions();
                window.dispatchEvent(new CustomEvent('suggestionUpdate', { detail: { roomId } }));
              } else {
                const error = await res.json();
                showToast(error.msg || '대안 일정 생성에 실패했습니다.');
              }
            } catch (error) {
              console.error('Failed to create alternative suggestion:', error);
              showToast('대안 일정 생성 중 오류가 발생했습니다.');
            }
          };

          return (
            <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200 shadow-inner">
              <h4 className="flex items-center gap-2 text-sm font-bold text-purple-800 mb-3">
                <span className="text-lg">✨</span> ARES 대안 일정 제안 (Chain Reschedule)
              </h4>
              <div className="space-y-2">
                {alternativeCandidates[suggestion._id].map((candidate, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleAlternativeSelect(candidate)}
                    className="flex items-center justify-between bg-white p-3 rounded-lg border border-purple-100 shadow-sm transition-all cursor-pointer hover:bg-purple-200 hover:shadow-md"
                  >
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-800 text-sm">
                        #{idx + 1} {candidate.date} ({candidate.day})
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        ⏰ {candidate.startTime} ~ {candidate.endTime} | 👥 {candidate.availableCount}/{candidate.totalMembers}명 가능
                      </span>
                    </div>
                    <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold font-mono">
                      Score: {candidate.score}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col ${
          isMobile ? 'w-full h-full' : 'w-full max-w-2xl max-h-[80vh]'
        }`}
      >
        {/* 헤더 */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">일정 관리</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* 탭 */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('future')}
            className={`flex-1 px-4 py-3 font-medium transition-colors ${
              activeTab === 'future'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            미래 약속 ({suggestions.future.length})
          </button>
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 px-4 py-3 font-medium transition-colors ${
              activeTab === 'today'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            오늘 약속 ({suggestions.today.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 px-4 py-3 font-medium transition-colors ${
              activeTab === 'past'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            지난 약속 ({suggestions.past.length})
          </button>
        </div>

        {/* 내용 */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : suggestions[activeTab].length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {activeTab === 'future' && '미래 약속이 없습니다.'}
              {activeTab === 'today' && '오늘 약속이 없습니다.'}
              {activeTab === 'past' && '지난 약속이 없습니다.'}
            </div>
          ) : (
            <div>{suggestions[activeTab].map(renderSuggestion)}</div>
          )}
        </div>
      </div>

      {/* 🆕 상세 모달 */}
      {/* [1단계 수정] memberLocations prop 제거 → ScheduleDetailModal이 roomId로 직접 서버 fetch해서 경로 표시 */}
      {/* [3단계 수정] key={selectedSuggestion?._id} → 제안 변경 시 완전 재마운트 → 지도 목적지 재초기화 */}
      <ScheduleDetailModal
        key={selectedSuggestion?._id || 'no-suggestion'}
        isOpen={!!selectedSuggestion}
        onClose={() => setSelectedSuggestion(null)}
        suggestion={selectedSuggestion}
        roomId={roomId}
      />

      {/* 🆕 충돌 확인 모달 - Portal로 body에 렌더링 */}
      {conflictModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '16px',
            isolation: 'isolate'
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" style={{ position: 'relative', zIndex: 1000000 }}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-800">기존 일정이 있습니다</h3>
            </div>

            {/* 충돌하는 일정 목록 */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-medium mb-2">충돌하는 일정:</p>
              {conflictModal.conflicts?.map((conflict, idx) => (
                <div key={idx} className="text-sm text-red-600 flex items-center gap-2 py-1">
                  <Clock size={14} />
                  <span>{conflict.title} ({conflict.time})</span>
                </div>
              ))}
            </div>

            {/* 새 일정 정보 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700 font-medium mb-2">참석하려는 일정:</p>
              <div className="text-sm text-blue-600">
                <div className="flex items-center gap-2 py-1">
                  <Calendar size={14} />
                  <span>{conflictModal.suggestion?.date}</span>
                </div>
                <div className="flex items-center gap-2 py-1">
                  <Clock size={14} />
                  <span>{conflictModal.suggestion?.startTime} ~ {conflictModal.suggestion?.endTime}</span>
                </div>
                <div className="font-medium mt-1">{conflictModal.suggestion?.summary}</div>
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center mb-4">
              기존 일정과 시간이 겹칩니다.<br />
              그래도 참석하시겠습니까?
            </p>

            {/* 버튼 */}
            <div className="flex gap-3">
              <button
                onClick={() => setConflictModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={() => handleForceAccept(conflictModal.suggestionId)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                참석하기
              </button>
            </div>
          </div>
        </div>
      , document.body)}
      <CustomAlertModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type="warning"
        showCancel={true}
        confirmText="확인"
        cancelText="취소"
      />
    </div>
  );
};

export default SuggestionModal;
